import { RoundButton } from '@cian/ui-kit';
import { IconClose16 } from '@cian/ui-kit/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useShowTracking } from './hooks/useShowTracking';
import { closeOfferPreview } from '../../../../common/actions/closeOfferPreview/closeOfferPreview';
import { onAddToFavorite as addToFavorites } from '../../../../common/actions/newFavorites/onAddToFavorite';
import { removeFromFavorites } from '../../../../common/actions/newFavorites/removeFromFavorite';
import { FavoriteButton } from '../../../../common/components/FavoriteButton';
import { OfferCardPreview } from '../../../../common/components/OfferCardPreview';
import { useOfferPreviewData } from '../../../../common/hooks/useOfferPreviewData';
import { IDailyRentOfferForMobileSite } from '../../../../common/repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { selectMlSearchSessionGuid } from '../../../../common/selectors/analytics';
import { selectListingMlRanking } from '../../../../common/selectors/listingOffers/selectListingMlRanking';
import { TThunkDispatch } from '../../../../common/types/redux';
import { defferedTrack } from '../../../../common/utils/tracking';
import {
  trackAddFavorite,
  trackBookClick,
  trackCall,
  trackCardClick,
  trackChatClick,
  trackMoreClick,
  trackRemoveFromFavorite,
  trackShowPhoneClick,
  trackSlideChange,
} from '../tracking';

export interface IOfferPreviewCardContainerProps {
  offer: IDailyRentOfferForMobileSite;
  className?: string;
  hasCloseControl?: boolean;
  isCompact: boolean;
  showButtons?: boolean;
  narrowButtons?: boolean;
}

export const OfferPreviewCardContainer = ({
  offer,
  className,
  isCompact,
  showButtons = false,
  narrowButtons = false,
  hasCloseControl = false,
}: IOfferPreviewCardContainerProps) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const isSliderEventTriggeredRef = React.useRef(false);
  const { trackingProps, offerLink, offerLinkRel, hasDates, showFavoriteButton } = useOfferPreviewData({
    offer,
    reason: 'pin_click',
  });
  const mlRanking = useSelector(selectListingMlRanking);
  const mlSearchSessionGuid = useSelector(selectMlSearchSessionGuid);

  useShowTracking({ trackingProps, mlRanking, mlSearchSessionGuid });

  const onSlideChange = React.useCallback(() => {
    if (!isSliderEventTriggeredRef.current) {
      trackSlideChange(trackingProps);
      isSliderEventTriggeredRef.current = true;
    }
  }, [trackingProps]);

  const onCardClick = React.useCallback(() => {
    trackCardClick(trackingProps);
  }, [trackingProps]);

  const onAddToFavorites = React.useCallback(() => {
    dispatch(addToFavorites(offer));
    trackAddFavorite(trackingProps);
  }, [dispatch, offer, trackingProps]);

  const onRemoveFromFavorites = React.useCallback(() => {
    dispatch(removeFromFavorites(offer));
    trackRemoveFromFavorite(trackingProps);
  }, [dispatch, offer, trackingProps]);

  const onMoreClick = React.useCallback(() => {
    trackMoreClick(trackingProps);
  }, [trackingProps]);

  const onBookClick = React.useCallback(() => {
    trackBookClick(trackingProps);
  }, [trackingProps]);

  const onCallClick = React.useCallback(() => {
    trackShowPhoneClick(trackingProps);
    defferedTrack(() => trackCall(trackingProps));
  }, [trackingProps]);

  const onChatClick = React.useCallback(() => {
    trackChatClick(trackingProps);
  }, [trackingProps]);

  const onClose = React.useCallback(() => {
    dispatch(closeOfferPreview());
  }, [dispatch]);

  const carouselControls = React.useMemo(
    () => (
      <>
        {showFavoriteButton && (
          <FavoriteButton isFavorite={offer.isFavorite} onAdd={onAddToFavorites} onRemove={onRemoveFromFavorites} />
        )}
        {hasCloseControl && (
          <RoundButton
            theme="fill_dark"
            size="M"
            onClick={onClose}
            title="Закрыть"
            icon={<IconClose16 color="brand-white" />}
            data-testid="OfferPreviewCloseButton"
          />
        )}
      </>
    ),
    [hasCloseControl, offer.isFavorite, onClose, onAddToFavorites, onRemoveFromFavorites, showFavoriteButton],
  );

  return (
    <OfferCardPreview
      className={className}
      offer={offer}
      offerLink={offerLink}
      offerLinkRel={offerLinkRel}
      hasDates={hasDates}
      isCompact={isCompact}
      carouselControls={carouselControls}
      showButtons={showButtons}
      narrowButtons={narrowButtons}
      onSlideChange={onSlideChange}
      onCardClick={onCardClick}
      onMoreClick={onMoreClick}
      onBookClick={onBookClick}
      onCallClick={onCallClick}
      onChatClick={onChatClick}
    />
  );
};

OfferPreviewCardContainer.displayName = 'OfferPreviewCardContainer';
